import { Anchor, Card, Group, Alert, Text, Stack, Box, Center, Loader, Button } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import BreadcrumbsNavigation from "@/components/breadcrumbs-navigation";
import {
  TemplateEditorContainer,
  type SaveState
} from "@/modules/relatorios/components/template-editor/TemplateEditorContainer";
import { VariableHelper } from "@/modules/relatorios/components/template-editor/VariableHelper";
import { useTemplatesManager, useQuestionarioSelect } from "@/modules/relatorios/hooks";

/** Minimum height for loading state */
const LOADING_MIN_HEIGHT = 400;

/** Editor panel flex basis */
const EDITOR_FLEX_BASIS = "70%";

/** Variable helper panel flex basis */
const HELPER_FLEX_BASIS = "30%";

/** Sticky top position for variable helper */
const HELPER_STICKY_TOP = 20;

/** Route path for templates list */
const TEMPLATES_LIST_PATH = "/configuracoes/diagnostico/templates";

/**
 * Template HTML Editor Page
 *
 * Full-featured page for editing template HTML with:
 * - Live HTML editor with syntax highlighting
 * - Variable helper panel for easy insertion
 * - HTML preview tab (sanitized)
 * - PDF preview tab
 * - Auto-save functionality
 * @example
 * ```tsx
 * <Route path="/configuracoes/diagnostico/templates/:id/editor" element={<TemplateEditorPage />} />
 * ```
 */
export function TemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [selectedQuestionarioId, setSelectedQuestionarioId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState | null>(null);
  const { templates, isLoading } = useTemplatesManager();
  const { questionarios, isLoading: isLoadingQuestionarios, error: questionariosError } = useQuestionarioSelect();

  // Find template by ID
  const template = templates.find((t) => t.id === id);

  const handleSaveSuccess = useCallback(() => {
    // Template saved successfully - could add notification here
  }, []);

  const handleSaveError = useCallback((error: Error) => {
    // eslint-disable-next-line no-console
    console.error("Error saving template:", error);
  }, []);

  const handleSaveStateChange = useCallback((state: SaveState) => {
    setSaveState(state);
  }, []);

  const handleSaveClick = useCallback(() => {
    saveState?.save();
  }, [saveState]);

  // Loading state
  if (isLoading) {
    return (
      <Center h={LOADING_MIN_HEIGHT}>
        <Loader />
      </Center>
    );
  }

  // Template not found
  if (!template) {
    return (
      <>
        <BreadcrumbsNavigation backTo={TEMPLATES_LIST_PATH}>
          <Anchor component={NavLink} to={TEMPLATES_LIST_PATH}>
            Templates de Relatórios
          </Anchor>
          <Text c="dimmed">Template não encontrado</Text>
        </BreadcrumbsNavigation>

        <Card withBorder>
          <Alert icon={<IconAlertCircle size={16} />} title="Erro" color="red">
            O template com ID{" "}
            <Text component="span" ff="monospace">
              {id}
            </Text>{" "}
            não foi encontrado.
          </Alert>
        </Card>
      </>
    );
  }

  return (
    <>
      {/* Header: Breadcrumbs + Save Button */}
      <Group justify="space-between" align="center" mb="md">
        <BreadcrumbsNavigation backTo={TEMPLATES_LIST_PATH}>
          <Anchor component={NavLink} to={TEMPLATES_LIST_PATH}>
            Templates de Relatórios
          </Anchor>
          <Text c="dimmed">{template.nome}</Text>
        </BreadcrumbsNavigation>

        <Group gap="sm">
          <Button onClick={handleSaveClick} loading={saveState?.isSaving} disabled={!saveState?.isValid}>
            Salvar
          </Button>
        </Group>
      </Group>

      {/* Main Content Card */}
      <Card
        withBorder
        shadow="md"
        styles={{
          section: {
            overflow: "auto"
          }
        }}
      >
        {/* Main Content: Editor + Variable Helper */}
        <Card.Section inheritPadding py="md">
          <Group align="flex-start" gap="md" wrap="nowrap">
            {/* Main Editor (70% width) */}
            <Box style={{ flex: `1 1 ${EDITOR_FLEX_BASIS}` }}>
              <TemplateEditorContainer
                templateId={id ?? ""}
                onSaveSuccess={handleSaveSuccess}
                onSaveError={handleSaveError}
                showVariableHelper={false}
                questionarioId={selectedQuestionarioId ?? undefined}
                hideSaveButton
                onSaveStateChange={handleSaveStateChange}
              />
            </Box>

            {/* Variable Helper Sidebar (30% width) */}
            <Box style={{ flex: `0 0 ${HELPER_FLEX_BASIS}`, position: "sticky", top: HELPER_STICKY_TOP }}>
              <VariableHelper
                questionarioId={selectedQuestionarioId ?? undefined}
                onQuestionarioChange={setSelectedQuestionarioId}
                questionarios={questionarios}
                isLoadingQuestionarios={isLoadingQuestionarios}
                questionariosError={!!questionariosError}
                compact
              />
            </Box>
          </Group>
        </Card.Section>

        {/* Tips Section */}
        <Card.Section inheritPadding py="md" withBorder>
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Dicas de uso:
            </Text>
            <Text size="xs" c="dimmed">
              • Use as abas <strong>Editor</strong>, <strong>Preview PDF</strong> e <strong>Imagens</strong> para editar
              e visualizar o template
            </Text>
            <Text size="xs" c="dimmed">
              • Na aba <strong>Imagens</strong>, adicione imagens de fundo via URL ou upload (JPG, PNG, WebP até 2MB)
            </Text>
            <Text size="xs" c="dimmed">
              • Use variáveis como <code>{"{{imagem.nome}}"}</code> no Editor para inserir imagens de fundo
            </Text>
            <Text size="xs" c="dimmed">
              • Clique em variáveis no painel à direita para copiar para a área de transferência
            </Text>
            <Text size="xs" c="dimmed">
              • Clique no botão <strong>Salvar</strong> para salvar suas alterações
            </Text>
          </Stack>
        </Card.Section>
      </Card>
    </>
  );
}
